import * as Sentry from '@sentry/node'
import * as SentryIntegrations from '@sentry/integrations'
import getConfig from 'next/config'

/**
 * @description Initializes Sentry with the DSN, release =ID and extra config.
 * This function is modified from https://github.com/zeit/next.js/blob/canary/examples/with-sentry/utils/sentry.js
 * Modified to get vaiables from next's getCofig rather than directly from node.
 * @returns {Object} Object containing intialized sentry object and configured captureException.
 * @returns {Object} Sentry; the initalized Sentry Object.
 * @returns {Function} captureException; a configured captureException.
 */

export default function getSentry() {
  const { publicRuntimeConfig } = getConfig()
  const { NODE_ENV, CGC_WEB_RELEASE_ID, CGC_WEB_SENTRY_DSN } = publicRuntimeConfig

  const sentryOptions = {
    dsn: CGC_WEB_SENTRY_DSN,
    release: CGC_WEB_RELEASE_ID,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    environment: NODE_ENV,
  }

  sentryOptions.integrations = [new SentryIntegrations.ExtraErrorData()]

  if (NODE_ENV !== 'production' && NODE_ENV !== 'staging') {
    // Don't send errors when in dev mode.
    sentryOptions.beforeSend = () => null

    // Instead, dump the errors to the console
    sentryOptions.integrations = [
      new SentryIntegrations.Debug({
        // Trigger DevTools debugger instead of using console.log
        debugger: false,
      }),
    ]
  }

  Sentry.init(sentryOptions)

  return {
    Sentry,
    captureException: (err, ctx) => {
      console.error(err)
      Sentry.configureScope((scope) => {
        if (err?.message) {
          // De-duplication currently doesn't work correctly for SSR / browser errors
          // so we force deduplication by error message if it is present
          scope.setFingerprint([err.message])
        }
        scope.setExtra('browser', !!process.browser)
        scope.setExtra('window', typeof window === 'undefined')
        if (err?.statusCode) {
          scope.setExtra('statusCode', err.statusCode)
        }
        if (err?.bodyText) {
          scope.setExtra('bodyText', err.bodyText)
        }
        if (err?.stack) {
          scope.setExtra('stack', err.stack)
        }

        const isSsr = typeof window === 'undefined'
        scope.setTag('ssr', isSsr)

        if (!isSsr) {
          scope.setExtra('query', window.location.search)
          scope.setExtra('pathname', window.location.pathname)
        }

        const networkError = err?.networkError
        if (networkError) {
          scope.setExtra('name', networkError.name)
          scope.setExtra('statusCode', networkError.statusCode)
          scope.setExtra('bodyText', networkError.bodyText)
          scope.setExtra('stack', networkError.stack)
        }

        if (ctx) {
          const { req, res, errorInfo } = ctx

          if (res && res.statusCode) {
            scope.setExtra('statusCode', res.statusCode)
          }

          if (req) {
            scope.setExtra('url', req.url)
            scope.setExtra('method', req.method)
            scope.setExtra('headers', req.headers)
            scope.setExtra('params', req.params)
            scope.setExtra('query', req.query)
          }

          if (errorInfo) {
            Object.keys(errorInfo).forEach((key) => scope.setExtra(key, errorInfo[key]))
          }
        }
      })

      return Sentry.captureException(err)
    },
  }
}
